import { HttpResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, finalize } from 'rxjs';

import { CustomPortalError } from '../../../../core/errors/models/CustomPortalError';
import { ErrorType } from '../../../../core/errors/models/ErrorType';
import { FileDownloadService } from '../../../../core/services/file-download.service';
import { LanguageService } from '../../../../core/services/language.service';
import { SnackbarService } from '../../../../core/services/snackbar.service';
import {
  CertificateBundleExportRequest,
  CertificateExportRequest,
  UserCertificateBundleExportRequest,
} from '../models/CertificateExportRequest';
import { CertificateService } from './certificate.service';

const IN_PROGRESS_KEY = 'portal.courses.certificates.exportInProgress';
const SUCCESS_KEY = 'portal.courses.certificates.exportSucceeded';
const FAILURE_KEY = 'portal.courses.certificates.exportFailed';
const NO_CERTIFICATE_FOR_USER_KEY =
  'portal.courses.certificates.noCertificateForUser';

@Injectable({
  providedIn: 'root',
})
export class CertificateExportService {
  private readonly certificateService = inject(CertificateService);
  private readonly fileDownloadService = inject(FileDownloadService);
  private readonly snackbar = inject(SnackbarService);
  private readonly languageService = inject(LanguageService);

  private readonly runningExports = signal(0);
  readonly isExporting = computed(() => this.runningExports() > 0);

  exportCertificate(request: CertificateExportRequest, fileName: string): void {
    this.startDownload(
      this.certificateService.getCertificate(request),
      fileName,
    );
  }

  exportCertificateBundle(
    request: CertificateBundleExportRequest,
    fileName: string,
  ): void {
    this.startDownload(
      this.certificateService.getCertificateBundle(request),
      fileName,
    );
  }

  exportUserCertificateBundle(
    request: UserCertificateBundleExportRequest,
    fileName: string,
  ): void {
    this.startDownload(
      this.certificateService.getUserCertificateBundle(request),
      fileName,
      NO_CERTIFICATE_FOR_USER_KEY,
    );
  }

  private startDownload(
    download: Observable<HttpResponse<Blob>>,
    fileName: string,
    emptyResultMessageKey?: string,
  ): void {
    const progressSnackbar = this.snackbar.progress(IN_PROGRESS_KEY);
    this.runningExports.update((running) => running + 1);

    download
      .pipe(
        finalize(() => {
          progressSnackbar.dismiss();
          this.runningExports.update((running) => running - 1);
        }),
      )
      .subscribe({
        next: (response) => {
          if (!response.body) {
            this.showFailure();
            return;
          }
          this.fileDownloadService.save(response.body, fileName);
          this.snackbar.success(SUCCESS_KEY);
        },
        error: (error: CustomPortalError) => {
          if (
            emptyResultMessageKey &&
            error.errorType === ErrorType.NOT_FOUND
          ) {
            this.snackbar.info(emptyResultMessageKey);
            return;
          }
          this.showFailure();
        },
      });
  }

  private showFailure(): void {
    this.snackbar.errorMessage(
      this.languageService.translateOnRuntime(FAILURE_KEY),
    );
  }
}
